import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  Building,
  TrendingUp,
  Shield,
  Clock,
  Layers,
  Sparkles,
  Search,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { FactsCycler } from "@/components/factsCycler";
import { AnalysisProgress, AIProgressRing } from "@/components/ui/ai-progress";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { runModelWithSSE } from "@/pages/upload_statement/runModelWithSSE";
import { validateFormOnSubmit } from "@/pages/upload_statement/validation";
import { useAuth } from "@/contexts/AuthContext";
import { incrementGuestAnalysis, getGuestAnalysisRemaining, getAllGuestLimits } from "@/utils/guestMode";
import { saveAnalysisResult } from "@/utils/userQuota";
import AuthModal from "@/components/AuthModal";
import { pages } from "@/Environment";
import { BENCHMARKS, industryOptionLabel } from "@/data/benchmarks.js";
import { trackEvent, trackAnalysisResult, trackAnalysisError } from "@/lib/analytics";
import { COMPANY_SEARCH_URL, MODEL_SSE_URL } from "@/Environment";

// Framework configuration
const FRAMEWORKS = {
  current: {
    id: "current",
    name: "Current Framework",
    questions: 48,
    description: "Standard CCLA assessment with 48 criteria",
    badge: null,
  },
  advanced: {
    id: "advanced",
    name: "Advanced Framework",
    questions: 61,
    description: "Includes TISC and International Reporting Template criteria",
    badge: "NEW",
  },
};

const FormStates = {
  Form: "form",
  Processing: "processing",
  Error: "error",
};

const analysisSteps = [
  {
    title: "Document processing",
    description: "Structure and references are normalised for accurate review.",
  },
  {
    title: "AI analysis",
    description: "Evidence is extracted against each criterion in the framework.",
  },
  {
    title: "Pattern recognition",
    description: "Gaps, strengths, and tone are surfaced across the report.",
  },
  {
    title: "Benchmark comparison",
    description: "Performance is compared against industry and index peers.",
  },
  {
    title: "Report generation",
    description: "Recommendations and timelines are prioritised for action.",
  },
];

export default function UploadStatement() {
  const navigate = useNavigate();
  const { user, isGuest, canAnalyze, getRemainingAnalyses, refreshQuota } = useAuth();
  const authStatus = user ? "registered" : isGuest ? "guest" : "anonymous";

  const [companyName, setCompanyName] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [homepageUrl, setHomepageUrl] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [companyPDF, setCompanyPDF] = useState(null);
  const [industry, setIndustry] = useState("");
  const [index, setIndex] = useState("");
  const [framework, setFramework] = useState("current");
  const [currentState, setCurrentState] = useState(FormStates.Form);
  const [errorText, setErrorText] = useState(null);
  const [processingStep, setProcessingStep] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showQuotaExceeded, setShowQuotaExceeded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const getCompanySearchUrl = useCallback(() => {
    if (COMPANY_SEARCH_URL && COMPANY_SEARCH_URL.trim().length > 0) {
      return COMPANY_SEARCH_URL.trim();
    }
    const base = (MODEL_SSE_URL || "").replace(/\/[^/]+$/, "");
    return `${base}/company_search`;
  }, []);

  // Debounced company search
  useEffect(() => {
    if (!companyName || companyName.trim().length < 3) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        setIsSearching(true);
        setSearchError(null);
        const url = `${getCompanySearchUrl()}?q=${encodeURIComponent(companyName.trim())}`;
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `HTTP ${response.status}`);
        }
        const data = await response.json();
        setSearchResults(data.results || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Company search failed:", err);
          setSearchError("Unable to fetch company matches. You can continue without selecting.");
          setSearchResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [companyName, getCompanySearchUrl]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === "application/pdf") {
        setCompanyPDF(file);
      } else {
        setErrorText("Please upload a PDF file.");
        setCurrentState(FormStates.Error);
      }
    }
  }, []);

  function activeState(states) {
    if (!Array.isArray(states)) {
      return currentState === states;
    }
    return states.includes(currentState);
  }

  // Get remaining analyses for the selected framework
  const getFrameworkRemaining = () => {
    if (isGuest) {
      return getGuestAnalysisRemaining(framework);
    }
    return getRemainingAnalyses(framework);
  };

  async function handleFormSubmit(event) {
    event.preventDefault();

    // The server enforces every limit. Guests are also stopped here by this browser's
    // own counter; registered users are not, so they can still open a statement that
    // was analysed before (cached results are not counted).
    if (isGuest && !canAnalyze(framework)) {
      const selectedFrameworkName = FRAMEWORKS[framework]?.name || framework;
      setShowQuotaExceeded(true);
      setCurrentState(FormStates.Error);
      setErrorText(`You've reached the free limit for ${selectedFrameworkName}. Sign up to continue.`);
      trackEvent("analysis_quota_gate", { auth_status: authStatus, framework });
      setShowAuthModal(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      await validateFormOnSubmit(companyName, companyPDF);
    } catch (error) {
      console.error("Error during form submission:", error);
      setCurrentState(FormStates.Error);
      setErrorText(`Error processing statement: ${error.message}`);
      trackAnalysisError(authStatus, "validation");
      return;
    }

    setCurrentState(FormStates.Processing);
    setProcessingStep(0);
    setAnalysisProgress(0);
    trackEvent("analysis_upload_started", {
      auth_status: authStatus,
      industry_provided: industry ? "yes" : "no",
      framework: framework,
    });

    try {
      await runModelWithSSE(
        companyName,
        companyPDF?.name ?? "",
        companyPDF,
        (progress) => {
          setAnalysisProgress(progress.progress_percent || 0);
          setProgressMessage(progress.message || "");
          if (progress.step) setProcessingStep(Math.min(progress.step - 1, analysisSteps.length - 1));
        },
        (result) => {
          trackAnalysisResult(authStatus, result?.analysis_metadata);

          const enhancedData = {
            ...result,
            company_info: {
              name: companyName,
              industry,
              index,
              company_number: companyNumber || null,
              homepage_url: homepageUrl || null,
            },
            // Peer comparisons are worked out on the results page from the current benchmark release
          };

          // Cached results are not counted by the server, so not here either
          const fromCache = result?.analysis_metadata?.cache_status === "cache-hit";
          if (isGuest) {
            if (!fromCache) incrementGuestAnalysis(framework);
          } else if (user) {
            saveAnalysisResult(user.uid, result);
            refreshQuota();
          }

          navigate(pages.REVIEW, { state: { data: JSON.stringify(enhancedData) } });
        },
        (error) => {
          setCurrentState(FormStates.Error);
          setErrorText(error.message);
          trackAnalysisError(authStatus, "runtime");
          if (error.code === "guest_limit" && isGuest) {
            setShowQuotaExceeded(true);
            setShowAuthModal(true);
          }
        },
        industry,  // Pass industry to backend for sector-contextualized analysis
        framework, // Pass framework type to backend
        companyNumber || null,
        homepageUrl || null
      );
    } catch (error) {
      setCurrentState(FormStates.Error);
      setErrorText(error.message);
    }
  }

  const remainingAnalyses = getFrameworkRemaining();
  const guestLimits = getAllGuestLimits();

  return (
    <main className="min-h-screen bg-[var(--surface-ambient)] pb-24 pt-28">
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-6">
        <header className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-4">
            <p className="eyebrow">
              Statement review
            </p>
            <h1 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
              Upload your modern slavery statement for a ready-to-share briefing.
            </h1>
            <p className="max-w-2xl text-base leading-relaxed text-[var(--text-secondary)]">
              We distil the language, evidence, and benchmarks that matter so your compliance programme can respond with
              clarity.
            </p>
          </div>

          <div className="flex max-w-sm items-center gap-3 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white px-5 py-4 shadow-[0_18px_36px_rgba(15,23,42,0.08)]">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[rgba(31,76,84,0.08)] text-[var(--accent)]">
              <Clock className="h-5 w-5" />
            </span>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                {isGuest
                  ? `${remainingAnalyses} guest review${remainingAnalyses === 1 ? "" : "s"} remaining`
                  : "Analyses remaining"}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                {isGuest
                  ? "Sign up for more analyses"
                  : `${remainingAnalyses} review${remainingAnalyses === 1 ? "" : "s"} this month.`}
              </p>
            </div>
          </div>
        </header>

        {/* Batch processing notice */}
        <div className="rounded-2xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/60 p-4">
          <p className="text-sm text-[var(--text-secondary)]">
            <span className="font-semibold text-[var(--text-primary)]">Processing at scale?</span>
            {' '}Batch processing of hundreds of statements available for research projects.{' '}
            <a
              href="mailto:m.gad1@lancaster.ac.uk"
              className="text-[var(--accent)] underline hover:no-underline"
            >
              Contact us
            </a>
          </p>
        </div>

        <AnimatePresence mode="wait">
          {activeState([FormStates.Form, FormStates.Error]) && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.6fr)]"
            >
              <section className="rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-10 shadow-[0_32px_64px_rgba(15,23,42,0.08)] backdrop-blur">
                <div className="space-y-6">
                  {showQuotaExceeded && isGuest && (
                    <Alert variant="info">
                      <AlertDescription>
                        Nice work! Create a free account for 10 monthly reviews and save your analysis history.
                      </AlertDescription>
                    </Alert>
                  )}

                  {!isGuest && remainingAnalyses <= 2 && remainingAnalyses > 0 && (
                    <Alert variant="info">
                      <AlertDescription>
                        You have {remainingAnalyses} analysis{remainingAnalyses === 1 ? "" : "es"} remaining this month.
                      </AlertDescription>
                    </Alert>
                  )}

                  {currentState === FormStates.Error && errorText && (
                    <Alert variant="danger">
                      <AlertDescription>{errorText}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleFormSubmit} encType="multipart/form-data" className="space-y-6">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]" htmlFor="companyName">
                        <Building className="h-4 w-4 text-primary-500" />
                        Company name
                      </label>
                      <input
                        id="companyName"
                        type="text"
                        value={companyName}
                        onChange={(event) => {
                          setCompanyName(event.target.value);
                          setCompanyNumber("");
                        }}
                        className="w-full rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                        placeholder="e.g. Lancaster Holdings"
                      />
                      <p className="text-xs text-[var(--text-soft)]">
                        Start typing to search Companies House. Select an exact match to lock the company number, or continue with your typed name.
                      </p>
                    </div>

                    {/* Companies House picker */}
                    <div className="space-y-2 rounded-2xl border border-[rgba(31,76,84,0.14)] bg-[var(--accent-light)]/40 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
                        <Search className="h-4 w-4 text-primary-500" />
                        Companies House search
                        <span className="ml-1 text-xs font-normal text-[var(--text-soft)]">(preferred for the advanced framework)</span>
                        {companyNumber && (
                          <span className="ml-auto flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[var(--accent)] shadow-sm">
                            <CheckCircle2 className="h-4 w-4" />
                            Selected #{companyNumber}
                          </span>
                        )}
                      </div>

                      <div className="space-y-2">
                        {searchError && (
                          <p className="text-xs text-warning-700">
                            {searchError}
                          </p>
                        )}

                        {!searchError && (
                          <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-white">
                            <div className="flex items-center justify-between border-b border-[rgba(31,76,84,0.08)] px-3 py-2 text-xs text-[var(--text-soft)]">
                              <span>
                                {companyName.trim().length < 3
                                  ? "Type at least 3 characters to search"
                                  : companyNumber
                                    ? "Match selected from Companies House"
                                    : searchResults.length === 0 && !isSearching
                                      ? "No matches yet"
                                      : "Select the exact legal entity"}
                              </span>
                              {isSearching && (
                                <span className="flex items-center gap-1 text-[var(--text-secondary)]">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Searching...
                                </span>
                              )}
                            </div>

                            <ul className="divide-y divide-[rgba(31,76,84,0.06)]">
                              {searchResults.map((result) => (
                                <li key={`${result.company_number}-${result.company_name}`}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCompanyName(result.company_name || "");
                                      setCompanyNumber(result.company_number || "");
                                      setSearchResults([]);
                                    }}
                                    className="flex w-full flex-col gap-1 px-3 py-3 text-left transition hover:bg-[var(--accent-light)]/60"
                                  >
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-semibold text-[var(--text-primary)]">
                                        {result.company_name}
                                      </p>
                                      {result.company_number && (
                                        <span className="rounded-full bg-[rgba(31,76,84,0.06)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-secondary)]">
                                          #{result.company_number}
                                        </span>
                                      )}
                                      {result.company_status && (
                                        <span className="rounded-full bg-[rgba(31,76,84,0.08)] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[var(--text-soft)]">
                                          {result.company_status}
                                        </span>
                                      )}
                                    </div>
                                    {result.registered_office_address && (
                                      <p className="text-xs text-[var(--text-soft)]">
                                        {result.registered_office_address}
                                      </p>
                                    )}
                                  </button>
                                </li>
                              ))}
                            </ul>

                            {companyNumber && (
                              <div className="flex items-center justify-between border-t border-[rgba(31,76,84,0.08)] px-3 py-2 text-xs text-[var(--text-soft)]">
                                <span>Using Companies House match for disambiguation.</span>
                                <button
                                  type="button"
                                  onClick={() => setCompanyNumber("")}
                                  className="text-[var(--accent)] underline-offset-2 hover:underline"
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]" htmlFor="homepageUrl">
                        <Building className="h-4 w-4 text-primary-500" />
                        Homepage URL (optional)
                      </label>
                      <input
                        id="homepageUrl"
                        type="url"
                        value={homepageUrl}
                        onChange={(event) => setHomepageUrl(event.target.value)}
                        className="w-full rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                        placeholder="https://www.example.com"
                      />
                      <p className="text-xs text-[var(--text-soft)]">
                        Optional: we will scan your homepage for a visible Modern Slavery Statement link (Q1).
                      </p>
                    </div>

                    {/* Framework Selector */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
                        <Layers className="h-4 w-4 text-primary-500" />
                        Assessment Framework
                      </label>
                      <div className="grid gap-3 md:grid-cols-2">
                        {Object.values(FRAMEWORKS).map((fw) => {
                          const isSelected = framework === fw.id;
                          const remaining = isGuest ? getGuestAnalysisRemaining(fw.id) : null;
                          return (
                            <button
                              key={fw.id}
                              type="button"
                              onClick={() => setFramework(fw.id)}
                              className={`relative flex flex-col gap-2 rounded-2xl border-2 p-4 text-left transition-all ${
                                isSelected
                                  ? "border-[var(--accent)] bg-[var(--accent-light)] shadow-[0_0_0_4px_rgba(31,76,84,0.08)]"
                                  : "border-[rgba(31,76,84,0.12)] bg-white hover:border-[rgba(31,76,84,0.3)] hover:bg-[var(--accent-light)]/40"
                              }`}
                            >
                              {fw.badge && (
                                <span className="absolute -top-2 right-3 rounded-full bg-gradient-to-r from-[#1f4c54] to-[#2a6470] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                                  {fw.badge}
                                </span>
                              )}
                              <div className="flex items-center gap-2">
                                {fw.id === "advanced" ? (
                                  <Sparkles className={`h-4 w-4 ${isSelected ? "text-[var(--accent)]" : "text-primary-500"}`} />
                                ) : (
                                  <Layers className={`h-4 w-4 ${isSelected ? "text-[var(--accent)]" : "text-primary-500"}`} />
                                )}
                                <span className={`text-sm font-semibold ${isSelected ? "text-[var(--accent)]" : "text-[var(--text-primary)]"}`}>
                                  {fw.name}
                                </span>
                                <span className={`ml-auto text-xs font-medium ${isSelected ? "text-[var(--accent)]" : "text-[var(--text-soft)]"}`}>
                                  {fw.questions} criteria
                                </span>
                              </div>
                              <p className="text-xs text-[var(--text-secondary)]">{fw.description}</p>
                              {isGuest && remaining !== null && (
                                <p className={`text-xs font-medium ${remaining > 0 ? "text-[var(--text-soft)]" : "text-warning-700"}`}>
                                  {remaining > 0
                                    ? `${remaining} guest review${remaining === 1 ? "" : "s"} remaining`
                                    : "No guest reviews remaining"}
                                </p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]" htmlFor="industry">
                          <TrendingUp className="h-4 w-4 text-primary-500" />
                          Industry (optional)
                        </label>
                        <select
                          id="industry"
                          value={industry}
                          onChange={(event) => setIndustry(event.target.value)}
                          className="w-full appearance-none rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                        >
                          <option value="">Select an industry</option>
                          {BENCHMARKS.industries.map((item) => (
                            <option key={item} value={item}>
                              {industryOptionLabel(item)}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-[var(--text-soft)]">
                          Adds sector-specific risks, examples, and best practices to your analysis.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]" htmlFor="index">
                          <Shield className="h-4 w-4 text-primary-500" />
                          Stock index (optional)
                        </label>
                        <select
                          id="index"
                          value={index}
                          onChange={(event) => setIndex(event.target.value)}
                          className="w-full appearance-none rounded-2xl border border-[rgba(31,76,84,0.16)] bg-white px-4 py-3 text-[var(--text-primary)] transition focus:border-[var(--accent)] focus:ring-4 focus:ring-[rgba(31,76,84,0.12)]"
                        >
                          <option value="">Select an index</option>
                          {BENCHMARKS.indices.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-[var(--text-soft)]">See how you compare within your index.</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]" htmlFor="companyPDF">
                        <FileText className="h-4 w-4 text-primary-500" />
                        Statement PDF
                      </label>
                      <input
                        id="companyPDF"
                        type="file"
                        accept=".pdf"
                        onChange={(event) => setCompanyPDF(event.target.files[0] ?? null)}
                        className="hidden"
                      />
                      <label
                        htmlFor="companyPDF"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`group flex h-44 w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed text-center transition cursor-pointer ${
                          isDragging
                            ? "border-[var(--accent)] bg-[var(--accent-light)] scale-[1.02]"
                            : "border-[rgba(31,76,84,0.18)] bg-[var(--accent-light)]/60 hover:border-[rgba(31,76,84,0.4)] hover:bg-white"
                        }`}
                      >
                        <span className={`flex h-12 w-12 items-center justify-center rounded-full text-[var(--accent)] transition group-hover:scale-105 ${
                          isDragging ? "bg-[var(--accent)]/20 scale-110" : "bg-[rgba(31,76,84,0.1)]"
                        }`}>
                          <Upload className="h-5 w-5" />
                        </span>
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">
                            {companyPDF ? companyPDF.name : isDragging ? "Drop your PDF here" : "Click to upload or drag and drop"}
                          </p>
                          <p className="text-xs text-[var(--text-soft)]">PDF files only, maximum 20MB.</p>
                        </div>
                      </label>
                    </div>

                    <Button type="submit" size="xl" className="w-full justify-center">
                      Begin analysis
                    </Button>
                  </form>
                </div>
              </section>

              <aside className="flex flex-col gap-8 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/60 p-8 shadow-[0_28px_56px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="space-y-4">
                  <p className="eyebrow">
                    What happens next
                  </p>
                  <ul className="space-y-4">
                    {analysisSteps.map((step, index) => (
                      <li key={step.title} className="flex gap-4">
                        <span className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl border border-[rgba(31,76,84,0.16)] text-sm font-semibold text-[var(--text-primary)]">
                          {index + 1}
                        </span>
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-[var(--text-primary)]">{step.title}</p>
                          <p className="text-sm text-[var(--text-secondary)]">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <FactsCycler />
              </aside>
            </motion.div>
          )}

          {activeState(FormStates.Processing) && (
            <motion.section
              key="processing"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="mx-auto flex w-full max-w-4xl flex-col gap-12 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-12 text-center shadow-[0_36px_72px_rgba(15,23,42,0.1)] backdrop-blur"
            >
              <div className="space-y-4">
                <p className="eyebrow">
                  Analysis in progress
                </p>
                <h2 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)]">
                  Reviewing {companyName || "your statement"} with full precision.
                </h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  We are extracting evidence, benchmarking performance, and composing your executive-ready briefing. This typically completes in under three minutes—please keep this window open.
                </p>
              </div>

              <div className="flex flex-col items-center gap-10">
                <AIProgressRing progress={analysisProgress} size={170} />
                {progressMessage && (
                  <p className="text-sm text-neutral-600 mt-2">{progressMessage}</p>
                )}
                <AnalysisProgress steps={analysisSteps} currentStep={processingStep} />
              </div>

              <div className="mx-auto w-full max-w-lg">
                <FactsCycler />
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setShowQuotaExceeded(false);
        }}
      />
    </main>
  );
}
